import { CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, Directive, EventEmitter, Injectable, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { PropertySearchWidgetLocation } from '../../services/direct.service';
import { DialogEditLocationComponent } from './dialog-edit-location/dialog-edit-location.component';

@Injectable()
export class DragAndDropManagerService {
  private list = new BehaviorSubject<string[]>([]);

  public onListChange = (): Observable<string[]> => this.list.asObservable();

  constructor() { }

  public register = (id: string) => {
    if (!id || this.list.value.includes(id)) {
      return;
    }
    this.list.next([...this.list.value, id]);
  };

  public unregister = (id: string) => {
    this.list.next(this.list.value.filter((x) => x !== id));
  };
}

@Directive({
  standalone: false,
  selector: '[dragAndDropManager]',
})
export class DragAndDropManagerDirective implements OnInit, OnDestroy {
  private manager!: Subscription;

  constructor(
    private dropList: CdkDropList,
    private managerService: DragAndDropManagerService
  ) { }

  ngOnInit(): void {
    this.managerService.register(this.dropList.id);
    this.manager = this.managerService.onListChange().subscribe((x) => {
      this.dropList.connectedTo = x;
    });
  }

  ngOnDestroy(): void {
    this.manager.unsubscribe();
  }
}

// Create a root directive that uses scoped drag&drop manager service.
// This allows us to have multiple independant nested drag&drop lists
@Directive({
  standalone: false,
  selector: '[dragAndDropManagerRoot]',
  providers: [
    {
      provide: DragAndDropManagerService,
    },
  ],
})
export class DragAndDropManagerRootDirective extends DragAndDropManagerDirective { }

@Component({
  standalone: false,
  selector: 'sbnb-direct-location-editor',
  templateUrl: './direct-location-editor.component.html',
})
export class DirectLocationEditorComponent {
  @Input() locations: PropertySearchWidgetLocation[] = [];
  @Output() locationsChange = new EventEmitter<Array<PropertySearchWidgetLocation>>();

  invert: boolean = true;
  onDragDrop$ = new Subject<CdkDragDrop<Array<PropertySearchWidgetLocation>>>();

  constructor(private dialog: MatDialog) { }

  ngOnInit() {
    this.onDragDrop$.subscribe(this.onDragDrop);
  }

  addLocation(parent?: PropertySearchWidgetLocation) {
    const dialogRef = this.dialog.open(DialogEditLocationComponent, {
      width: '720px',
      maxHeight: '90vh',
      data: {
        mode: 'create',
        location: {
          id: '',
          name: '',
          lat: '',
          lng: '',
          children: [],
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (parent) {
        // find the right parent to push the result to
        const parentLocation = this.findLocation(this.locations, parent.name);
        if (parentLocation) {
          parentLocation.children.push(result);
          this.locationsChange.emit(this.locations);
        }
        return;
      }

      if (result.name) {
        this.locations.push(result);
        this.locationsChange.emit(this.locations);
      }
    });
  }

  // Recursive function to find the location by name
  private findLocation(
    locations: PropertySearchWidgetLocation[],
    name: string
  ): PropertySearchWidgetLocation | undefined {
    for (const location of locations) {
      if (location.name === name) {
        return location;
      }
      if (location.children) {
        const foundLocation = this.findLocation(location.children, name);
        if (foundLocation) {
          return foundLocation;
        }
      }
    }
    return undefined;
  }

  editLocation(location: PropertySearchWidgetLocation) {
    // open the edit dialog
    const dialogRef = this.dialog.open(DialogEditLocationComponent, {
      width: '720px',
      maxHeight: '90vh',
      data: {
        location: location,
        mode: 'update',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // todo: Emit to parent and update the location
    });
  }

  removeLocation(location: PropertySearchWidgetLocation) {
    this.locations = this.removeItemById([...this.locations], location.name);

    this.locationsChange.emit(this.locations);
  }

  private removeItemById(items: any[], itemName: string): any[] {
    return items.reduce((newItems, item) => {
      // Check if the current item is the one to remove
      if (item.name !== itemName) {
        let newItem = { ...item };
        // If the current item has children, create a new array for children too
        if (item.children) {
          newItem.children = this.removeItemById(item.children, itemName);
        }
        newItems.push(newItem);
      }
      return newItems;
    }, []);
  }

  private onDragDrop = (event: CdkDragDrop<Array<PropertySearchWidgetLocation>>) => {
    if (event.container === event.previousContainer) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  };
}
